import { getRuntimeConfigValue } from '@mbbe/config';

type TurnstileWidgetId = string;

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      size?: 'invisible' | 'normal' | 'compact';
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    }
  ) => TurnstileWidgetId;
  execute: (widgetId: TurnstileWidgetId) => void;
  reset: (widgetId: TurnstileWidgetId) => void;
  remove: (widgetId: TurnstileWidgetId) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

export function getTurnstileSiteKey(): string {
  return getRuntimeConfigValue('TURNSTILE_SITE_KEY', import.meta.env.VITE_TURNSTILE_SITE_KEY || '');
}

export function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile-script]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export async function getTurnstileToken(container: HTMLElement): Promise<string> {
  const siteKey = getTurnstileSiteKey();
  if (!siteKey) {
    throw new Error('Turnstile site key is missing');
  }

  await loadTurnstileScript();

  return new Promise((resolve, reject) => {
    if (!window.turnstile) {
      reject(new Error('Turnstile is not available'));
      return;
    }

    let widgetId: TurnstileWidgetId | undefined;
    const cleanup = () => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };

    widgetId = window.turnstile.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      callback: (token) => {
        cleanup();
        resolve(token);
      },
      'error-callback': () => {
        cleanup();
        reject(new Error('Güvenlik doğrulaması başarısız oldu.'));
      },
      'expired-callback': () => {
        cleanup();
        reject(new Error('Güvenlik doğrulamasının süresi doldu.'));
      },
    });
    window.turnstile.execute(widgetId);
  });
}
